import { TFnType } from '@tolgee/react';
import { isEmpty, compact, isNil, sortBy, get } from 'lodash-es';
import { useCallback, useEffect, useRef } from 'react';
// 排除角色名单
export const excludeRoleList = ['超级管理员'] as const;
// 不参与咨询分配名单
export const excludeConversation = ['超级管理员', '管理员'];
export const defaultRoleList = ['超级管理员', '管理员', '客服'] as const;

// 是否是官方默认超管角色
export const isDefaultSupperAdmin = (roleName: string, isDefault?: Java.NumberBool) => {
  return roleName === '超级管理员' && isDefault === 1;
};

// 是否是官方默认管理员角色
export const isDefaultAdmin = (roleName: string, isDefault?: Java.NumberBool) => {
  return roleName === '管理员' && isDefault === 1;
};

// 是否是官方默认客服角色
export const isDefaultAssistant = (roleName: string, isDefault?: Java.NumberBool) => {
  return roleName === '客服' && isDefault === 1;
};

/**
 * 提取roleIds数组
 * @param input
 * @returns
 */
export function parseDepartmentRolesField(input?: Partial<StoreHomeV2.DepartmentAndRoleType>[]) {
  if (isEmpty(input)) {
    return [];
  }
  return compact(input!.map((d) => d.roleId));
}

export function defaultRoleTranslate() {}

/**
 * 账号根据状态排序
 * 启用 > 休眠 > 停用
 * 启用: userStatus = Enabled && sleepFlag = false
 * 休眠: userStatus = enabled && sleepFlag = true
 * 停用： userStatus = disabled
 */
export function accountOrderByStatus<T extends any[] | undefined>(
  array: T,
  compareField: 'userStatus' | 'status',
) {
  if (isNil(array)) {
    return [];
  }
  return sortBy(array, (value) => {
    const status = get(value, compareField, undefined);
    if (isNil(status)) {
      return 0;
    }
    if (status === 'ENABLE' && value.sleepFlag) {
      return 1;
    }
    if (status === 'ENABLE' && !value.sleepFlag) {
      return 0;
    }
    return 2;
  });
}

/**
 * 角色id与角色类型的映射
 * @param roles
 * @returns 角色类型
 */
export function useRoleMap(roles?: StoreHomeV2.DepartmentDetail['roles']) {
  const roleMap = useRef(new Map<number, StoreHomeV2.RoleWithDepartment>());
  useEffect(() => {
    if (!isEmpty(roles)) {
      for (const role of roles!) {
        roleMap.current.set(role.id, role);
      }
    }
  }, [roles]);
  return {
    ...roleMap,
    get: useCallback(
      (roleId?: number) => {
        if (isNil(roleId)) {
          return undefined;
        }
        return roleMap.current.get(roleId);
      },
      [roleMap],
    ),
  };
}

/**
 * @region  translate
 */

export function translateDepartment(
  { name, isDefault, type }: Pick<StoreHomeV2.Department, 'name' | 'isDefault' | 'type'>,
  t: TFnType,
  showType = false,
) {
  const deptName = isDefault === 1 && name === '客服部' ? t('department.客服部') : name;
  const deptType = showType && type === 2 ? `(${t('dept_tag_us')})` : '';
  return `${deptName}${deptType}`;
}

/**
 * 翻译默认角色
 * @param roleName
 * @param t
 * @returns
 */
export function translateRole(
  roleName: StoreHomeV2.RoleWithDepartment['name'] | undefined,
  t: TFnType,
) {
  if (isNil(roleName)) {
    return '';
  }
  if (defaultRoleList.some((dr) => dr === roleName)) {
    return t(`department.${roleName as (typeof defaultRoleList)[number]}`);
  }
  return roleName;
}

/**
 * 翻译树形的部门角色
 * @param input
 * @param t
 * @returns
 */
export function translateTreeDepartment(
  input: StoreHomeV2.DepartmentWithRoles[] | undefined,
  t: TFnType,
) {
  if (isNil(input)) {
    return [];
  }
  return input.map((d) => {
    const roles = d.roles.map((r) => {
      return {
        ...r,
        name: translateRole(r.name, t),
      };
    });
    return {
      ...d,
      roles,
      name: translateDepartment(d, t),
    };
  });
}

/**
 * 翻译平铺的部门角色
 * @param input
 * @param t
 * @returns
 */
export function translateFlatDepartmentAndRole(
  input: StoreHomeV2.AccountInDepartment[] | undefined,
  t: TFnType,
) {
  if (isNil(input)) {
    return [];
  }
  return input.map((v) => {
    return {
      ...v,
      roleName: translateRole(v.roleName, t),
      departmentName: translateDepartment({ name: v.departmentName }, t),
    };
  });
}
